/**
 * Expanded detail for a report.
 */
import * as React from 'react';
import {
  FileText,
  Calendar,
  User,
  Star,
  CheckCircle2,
  Building2,
  Minus,
  Users,
  BookOpen,
  MessageSquare,
  GraduationCap,
  Info,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailsCard } from '@/components/common/DetailsCard';
import { ParticipantChip } from '@/components/common/ParticipantChip';
import { partitionParticipantsForReport } from '@/components/reports/inperson/report-form-utils';
import {
  REPORT_STATUS_CONFIG,
  SESSION_REPORT_FEELING_LABELS,
} from '@/components/reports/report-configs';
import { Spinner } from '@/components/ui/spinner';
import { getSession } from '@/services/inperson/sessions.service';
import { getSubjects } from '@/services/subjects.service';
import { useConnectedUser } from '@/hooks/useConnectedUser';
import { usePageActions } from '@/hooks/usePageActions';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import {
  buildTutorReportSections,
  buildReportValidationOptions,
  getDisplayAuthorFeedback,
  getIncompleteTutorsBannerText,
  isMultiAuthorReport,
} from '@/utils/report-author-feedback-utils';
import type {
  SessionReport,
  SessionReportAuthorFeedback,
  SessionReportStudentFeedback,
} from '@/types/inperson/report.types';
import type { AttendanceStatus } from '@/types/attendance.types';
import type { SessionWithParticipants } from '@/types/inperson/session.types';
import {
  compareReportParticipantsByRole,
  formatReportFilledAtLong,
  formatReportSessionLongDate,
  getReportAuthorFeedbackCounts,
  getReportDetailAuthorName,
  getReportDetailStatusColor,
  getReportValidatorName,
} from './report-table-utils';

/** Read-only five-star rating with a numeric score. */
function StarRating({
  rating,
  sizeClass,
  scoreClassName,
}: Readonly<{ rating: number; sizeClass: string; scoreClassName: string }>) {
  return (
    <span className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={`${sizeClass} ${
            i < rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'
          }`}
        />
      ))}
      <span className={scoreClassName}>{rating}/5</span>
    </span>
  );
}

/** Centered spinner shown while report detail loads. */
function ReportDetailLoader() {
  return (
    <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 py-16">
      <Spinner size="lg" label="Chargement du compte rendu…" />
    </div>
  );
}

/** Incomplete-tutors banner for viewers who can validate. */
function ReportValidationHint({
  report,
  session,
  viewerUserId,
}: Readonly<{
  report: SessionReport;
  session?: SessionWithParticipants | null;
  viewerUserId?: string;
}>) {
  const { canValidate } = usePageActions('reports');
  if (!canValidate) return null;

  const validateOptions = buildReportValidationOptions(report, session, viewerUserId);
  const bannerText = getIncompleteTutorsBannerText(report, validateOptions);
  if (!bannerText) return null;

  return (
    <div className="flex items-start gap-2 rounded-lg border border-[color-mix(in_srgb,var(--color-warning)_35%,transparent)] bg-[color-mix(in_srgb,var(--color-warning)_8%,transparent)] px-3 py-2.5 text-sm text-foreground">
      <Info
        className="w-4 h-4 shrink-0 mt-0.5"
        style={{ color: 'rgb(var(--color-warning))' }}
        aria-hidden
      />
      <span>
        <span className="font-medium">Validation : </span>
        {bannerText}
      </span>
    </div>
  );
}

/** Read-only tutor rating / feeling / comment block. */
export function TutorFeedbackReadOnly({
  feedback,
}: Readonly<{ feedback: SessionReportAuthorFeedback }>) {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-2">Note de la séance</p>
        {feedback.rating !== null ? (
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-5 h-5 ${star <= (feedback.rating ?? 0) ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`}
              />
            ))}
            <span className="ml-2 text-sm text-foreground">{feedback.rating}/5</span>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Aucune note attribuée</p>
        )}
      </div>

      {feedback.feeling != null && (
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">
            Comment vous êtes-vous senti pendant cette séance
          </p>
          <p className="text-sm text-foreground">
            {SESSION_REPORT_FEELING_LABELS[feedback.feeling]}
          </p>
        </div>
      )}

      <div>
        <p className="text-sm font-medium text-muted-foreground mb-1">Commentaire</p>
        {feedback.comment?.trim() ? (
          <p className="text-sm text-foreground whitespace-pre-wrap">{feedback.comment}</p>
        ) : (
          <p className="text-sm text-muted-foreground">Aucun commentaire rédigé</p>
        )}
      </div>
    </div>
  );
}

/** Props for {@link ReportSubjectsBlock}. */
interface ReportSubjectsBlockProps {
  report: SessionReport;
  subjectsById: Map<string, string>;
}

/** Subjects covered in the report, or an empty placeholder. */
const ReportSubjectsBlock: React.FC<ReportSubjectsBlockProps> = ({ report, subjectsById }) => {
  const ids = report.subjectIds ?? [];

  function renderSubjectChip(id: string): React.ReactNode {
    return (
      <span
        key={id}
        className="inline-flex items-center rounded-md border border-border bg-muted/50 px-2.5 py-0.5 text-sm text-foreground"
      >
        {subjectsById.get(id) ?? id}
      </span>
    );
  }

  if (ids.length === 0) {
    return (
      <DetailsCard icon={BookOpen} title="Matières" colorName="color-2" colSpan="full">
        <EmptyPlaceholder message="Aucune matière renseignée" />
      </DetailsCard>
    );
  }

  return (
    <DetailsCard icon={BookOpen} title="Matières" colorName="color-2" colSpan="full">
      <div className="flex flex-wrap gap-2">{ids.map(renderSubjectChip)}</div>
    </DetailsCard>
  );
};

/** Props for {@link ReportPresencesBlock}. */
interface ReportPresencesBlockProps {
  session: SessionWithParticipants | undefined;
  isError: boolean;
}

/** Attendance and volunteer presence cards for the report details. */
const ReportPresencesBlock: React.FC<ReportPresencesBlockProps> = ({ session, isError }) => {
  if (isError) {
    return (
      <DetailsCard icon={Users} title="Présences" colorName="color-3" colSpan="full">
        <EmptyPlaceholder message={getErrorMessage('loadParticipantsFailed')} />
      </DetailsCard>
    );
  }

  if (!session?.participants?.length) {
    return (
      <DetailsCard icon={Users} title="Présences" colorName="color-3" colSpan="full">
        <EmptyPlaceholder message="Aucun participant pour cette séance" />
      </DetailsCard>
    );
  }

  const { vscParticipants, attendanceParticipants } = partitionParticipantsForReport(
    session.participants,
    session.studentTutorPairs,
  );

  const sortedAttendance = [...attendanceParticipants].sort(compareReportParticipantsByRole);
  const hasAttendance = attendanceParticipants.length > 0;
  const showEmptyAttendance = !hasAttendance && vscParticipants.length === 0;
  const volunteerLabel = getRoleLabel('volunteer');
  const vscCaption =
    vscParticipants.length === 1
      ? `${volunteerLabel} présent sur la séance (hors saisie de présence dans ce compte rendu).`
      : `${getRoleLabelPlural('volunteer')} présents sur la séance (hors saisie de présence dans ce compte rendu).`;

  return (
    <>
      {vscParticipants.length > 0 && (
        <DetailsCard
          icon={Users}
          title={getRoleLabel('volunteer')}
          colorName="color-3"
          colSpan="full"
        >
          <p className="text-sm text-muted-foreground mb-3">{vscCaption}</p>
          <ul className="flex flex-wrap gap-2 list-none p-0 m-0">
            {vscParticipants.map((p) => (
              <li key={p.id}>
                <ParticipantChip
                  user={p.user}
                  role="volunteer"
                  volunteerRoleLabel={getRoleLabel('volunteer')}
                  size="md"
                  showTooltip
                />
              </li>
            ))}
          </ul>
        </DetailsCard>
      )}
      {hasAttendance && (
        <DetailsCard icon={Users} title="Présences" colorName="color-3" colSpan="full">
          <div className="rounded-lg border border-border bg-muted/25 p-4">
            <ul className="flex flex-col gap-3 list-none p-0 m-0">
              {sortedAttendance.map((p) => (
                <li key={p.id} className="flex items-start min-w-0">
                  <ParticipantChip
                    user={p.user}
                    role={p.role}
                    size="md"
                    showTooltip
                    showAbsenceReasonInline
                    attendanceStatus={(p.attendanceStatus || 'pending') as AttendanceStatus}
                    absenceJustification={p.absenceJustification ?? null}
                  />
                </li>
              ))}
            </ul>
          </div>
        </DetailsCard>
      )}
      {showEmptyAttendance && (
        <DetailsCard icon={Users} title="Présences" colorName="color-3" colSpan="full">
          <EmptyPlaceholder message="Aucun participant pour cette séance" />
        </DetailsCard>
      )}
    </>
  );
};

/** Props for {@link StudentFeedbacksBlock}. */
interface StudentFeedbacksBlockProps {
  feedbacks: SessionReportStudentFeedback[];
  session: SessionWithParticipants | undefined;
}

/** Per-student pedagogical follow-up block on the report details. */
const StudentFeedbacksBlock: React.FC<StudentFeedbacksBlockProps> = ({ feedbacks, session }) => {
  const nonEmpty = feedbacks.filter(
    (fb) => fb.workedItemIds.length > 0 || fb.rating !== null || fb.comment?.trim(),
  );

  if (!nonEmpty.length) return null;

  const participantMap = new Map((session?.participants ?? []).map((p) => [p.userId, p]));

  return (
    <DetailsCard
      icon={GraduationCap}
      title="Suivi pédagogique par élève"
      colorName="color-2"
      colSpan="full"
    >
      <div className="space-y-5">
        {nonEmpty.map((fb) => {
          const participant = participantMap.get(fb.participantUserId);
          const displayName =
            participant?.user?.displayName ||
            [participant?.user?.firstName, participant?.user?.lastName].filter(Boolean).join(' ') ||
            getRoleLabel('student');

          return (
            <div
              key={fb.participantUserId}
              className="rounded-lg border border-border bg-muted/25 p-4 space-y-3"
            >
              <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                <GraduationCap className="w-3.5 h-3.5 text-muted-foreground" />
                {displayName}
              </p>

              {fb.workedItemIds.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Devoirs / matières travaillés
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {(fb.workedItems?.length
                      ? fb.workedItems
                      : fb.workedItemIds.map((id) => ({ id, label: id }))
                    ).map((item) => (
                      <span
                        key={typeof item === 'string' ? item : item.id}
                        className="inline-flex items-center rounded-md border border-border bg-background px-2.5 py-0.5 text-sm text-foreground"
                      >
                        {typeof item === 'string' ? item : item.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {fb.rating !== null && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                    Note individuelle
                  </p>
                  <StarRating
                    rating={fb.rating}
                    sizeClass="w-4 h-4"
                    scoreClassName="ml-1.5 text-sm text-muted-foreground"
                  />
                </div>
              )}

              {fb.comment?.trim() && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                    Commentaire
                  </p>
                  <blockquote className="border-l-4 border-[var(--color-2-nuance-1)] pl-3 text-sm text-foreground whitespace-pre-wrap italic">
                    {fb.comment}
                  </blockquote>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </DetailsCard>
  );
};

/** Props for {@link ReportDetails}. */
export interface ReportDetailsProps {
  report: SessionReport | null | undefined;
  isReportLoading?: boolean;
}

/** Expanded in-person report details (GET entity). */
export const ReportDetails: React.FC<ReportDetailsProps> = ({
  report,
  isReportLoading = false,
}) => {
  const sessionId = report?.session?.id ?? report?.sessionId ?? '';
  const { user } = useConnectedUser();

  const sessionQuery = useQuery({
    queryKey: ['session', sessionId, 'report-detail'],
    queryFn: () => getSession(sessionId),
    enabled: !!sessionId && !!report && !isReportLoading,
  });

  const subjectsQuery = useQuery({
    queryKey: ['subjects', 'report-detail'],
    queryFn: () => getSubjects({ page: 1, pageSize: 20 }),
    select: (r) => r.data,
    enabled: !!report && !isReportLoading,
  });

  const isDetailLoading =
    isReportLoading ||
    !report ||
    subjectsQuery.isLoading ||
    (!!sessionId && sessionQuery.isLoading);

  if (isDetailLoading) {
    return <ReportDetailLoader />;
  }

  if (sessionId && sessionQuery.isError) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center py-12">
        <EmptyPlaceholder message={getErrorMessage('loadSessionDataFailed')} />
      </div>
    );
  }

  const session = sessionQuery.data;
  const subjectsById = new Map((subjectsQuery.data ?? []).map((s) => [s.id, s.label]));

  const statusConfig = REPORT_STATUS_CONFIG[report.status] || REPORT_STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  const authorName = getReportDetailAuthorName(report);
  const validatorName = getReportValidatorName(report);
  const sessionDate = formatReportSessionLongDate(report);
  const displayFeedback = getDisplayAuthorFeedback(report);
  const multiAuthor = isMultiAuthorReport(session, report);
  const sections = buildTutorReportSections(session, report);
  const { feedbackCount, completedCount } = getReportAuthorFeedbackCounts(report);
  const singleSection = sections.length === 1 ? sections[0] : null;

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <FileText className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="text-2xl font-bold text-[var(--color-1-nuance-1)] capitalize">
                Compte-rendu de session
              </h3>
              <DetailBadge
                label={statusConfig.label}
                icon={StatusIcon}
                colorName={getReportDetailStatusColor(report.status)}
                variant="filled"
              />
              {displayFeedback?.rating != null && displayFeedback.rating >= 4 && !multiAuthor && (
                <DetailBadge
                  label={`${displayFeedback.rating}/5 étoiles`}
                  icon={Star}
                  colorName="color-2"
                  variant="outlined"
                />
              )}
              {multiAuthor && feedbackCount > 0 && (
                <DetailBadge
                  label={`${completedCount}/${feedbackCount} évaluations`}
                  icon={Star}
                  colorName="color-2"
                  variant="outlined"
                />
              )}
            </div>
            {sessionDate && (
              <p className="text-base text-[var(--color-1-nuance-2)]">{sessionDate}</p>
            )}
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        {report.session && (
          <DetailsCard icon={Calendar} title="Séance" colorName="color-2" colSpan={1}>
            <div className="space-y-2">
              <DetailField label="Date" value={sessionDate || '—'} />
              <DetailField label="Date saisie" value={formatReportFilledAtLong(report.filledAt)} />
              {report.session.establishment?.name && (
                <DetailField
                  icon={Building2}
                  label="Établissement"
                  value={report.session.establishment.name}
                />
              )}
            </div>
          </DetailsCard>
        )}

        <DetailsCard icon={User} title="Auteur du CR" colorName="color-1" colSpan={1}>
          <DetailField label="Nom" value={authorName} />
        </DetailsCard>

        <ReportSubjectsBlock report={report} subjectsById={subjectsById} />

        <div className="col-span-full">
          <ReportValidationHint
            report={report}
            session={sessionQuery.data}
            viewerUserId={user?.id}
          />
        </div>

        {session?.type === 'information_meeting_in_person' ? (
          <DetailsCard
            icon={MessageSquare}
            title="Synthèse — Réunion d'information"
            colorName="color-1"
            colSpan="full"
          >
            {displayFeedback?.rating != null || displayFeedback?.comment ? (
              <div className="space-y-4">
                {displayFeedback.rating != null && (
                  <DetailField
                    label="Niveau d'engagement des participants"
                    value={
                      <StarRating
                        rating={displayFeedback.rating}
                        sizeClass="w-3.5 h-3.5"
                        scoreClassName="ml-1 text-xs text-muted-foreground"
                      />
                    }
                    vertical
                  />
                )}
                {displayFeedback.comment?.trim() && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      Synthèse de la réunion d'information
                    </p>
                    <blockquote className="border-l-4 border-[var(--color-1-nuance-1)] pl-3 text-sm text-foreground whitespace-pre-wrap italic">
                      {displayFeedback.comment}
                    </blockquote>
                  </div>
                )}
              </div>
            ) : (
              <EmptyPlaceholder message="Synthèse non encore saisie" />
            )}
            {report.updatedAt && (
              <p className="mt-3 text-xs text-muted-foreground border-t border-border pt-3">
                Dernière mise à jour :{' '}
                {format(new Date(report.updatedAt), 'd MMM yyyy à HH:mm', { locale: fr })}
              </p>
            )}
          </DetailsCard>
        ) : (
          <>
            {multiAuthor && sections.length > 0 && (
              <>
                {sections.map((section) => (
                  <DetailsCard
                    key={section.authorUserId}
                    icon={GraduationCap}
                    title={`${getRoleLabel(section.role === 'volunteer' ? 'volunteer' : 'tutor')} — ${section.displayName}`}
                    colorName="color-2"
                    colSpan="full"
                  >
                    <TutorFeedbackReadOnly feedback={section.feedback} />
                  </DetailsCard>
                ))}
              </>
            )}

            {!multiAuthor && (singleSection || displayFeedback) && (
              <DetailsCard icon={Star} title="Évaluation" colorName="color-3" colSpan="full">
                <TutorFeedbackReadOnly feedback={(singleSection?.feedback ?? displayFeedback)!} />
              </DetailsCard>
            )}
          </>
        )}

        {sessionId && <ReportPresencesBlock session={session} isError={sessionQuery.isError} />}

        {(report.studentFeedbacks?.length ?? 0) > 0 && (
          <StudentFeedbacksBlock feedbacks={report.studentFeedbacks!} session={session} />
        )}

        {report.status === 'validated' && validatorName && report.validatedAt && (
          <DetailsCard icon={CheckCircle2} title="Validation" colorName="color-3" colSpan="full">
            <div className="space-y-2">
              <DetailField label="Validé par" value={validatorName} />
              <DetailField
                label="Date"
                value={format(new Date(report.validatedAt), 'd MMMM yyyy à HH:mm', { locale: fr })}
              />
            </div>
          </DetailsCard>
        )}
      </div>

      {report.createdAt && (
        <div className="mt-8 pt-4 border-t border-[var(--color-1-nuance-3)]">
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <span>Créé le {new Date(report.createdAt).toLocaleDateString('fr-FR')}</span>
            {report.updatedAt && (
              <>
                <Minus className="w-3 h-3" />
                <span>Modifié le {new Date(report.updatedAt).toLocaleDateString('fr-FR')}</span>
              </>
            )}
            {!!report.version && (
              <>
                <Minus className="w-3 h-3" />
                <span>Version {report.version}</span>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

/** Options passed into {@link renderReportDetails}. */
export interface RenderReportDetailsOptions {
  isReportLoading?: boolean;
}

/** Detail renderer for an in-person report ({@link DataTable} expanded-row collapse). */
export const renderReportDetails = (
  report: SessionReport | null | undefined,
  options?: RenderReportDetailsOptions,
): React.ReactNode => <ReportDetails report={report} isReportLoading={options?.isReportLoading} />;
